import type { CanvasEntity, ConnectorData, GroupData, TaskData } from '../entityTypes';
import { generateEntityId } from '../entityTypes';
import type { GlobalTask } from '../../types';
import type { CanvasClipboard } from './types';
import { GRID_SIZE } from '../types';

export interface PastePayload {
  entities: CanvasEntity[];
  newGlobalTasks: GlobalTask[];
  newEntityIds: string[];
}

/**
 * Prepare a paste payload from a clipboard.
 * Generates fresh IDs for all entities and remaps all internal references.
 */
export function preparePastePayload(
  clipboard: CanvasClipboard,
  targetCenter: { x: number; y: number },
  activeLayerId: string,
  validLayerIds: Set<string>,
  getNextTaskId: () => string,
): PastePayload {
  // 1. Build ID remap table
  const idRemap = new Map<string, string>();
  const taskIdRemap = new Map<string, string>(); // old taskId -> new taskId

  for (const entity of clipboard.entities) {
    let newId: string;
    switch (entity.kind) {
      case 'task': {
        newId = getNextTaskId();
        const td = entity.data as unknown as TaskData;
        taskIdRemap.set(td.taskId, newId);
        break;
      }
      case 'textblock':
        newId = generateEntityId('tb');
        break;
      case 'textlabel':
        newId = generateEntityId('tl');
        break;
      case 'sprite':
        newId = generateEntityId('sp');
        break;
      case 'image':
        newId = generateEntityId('img');
        break;
      case 'blocknode':
        newId = generateEntityId('bn');
        break;
      case 'shape':
        newId = generateEntityId('shp');
        break;
      case 'group':
        newId = generateEntityId('grp');
        break;
      case 'waypoint':
        newId = generateEntityId('wp');
        break;
      case 'connector':
        newId = generateEntityId('conn');
        break;
      default:
        newId = generateEntityId('ent');
    }
    idRemap.set(entity.id, newId);
  }

  // 2. Position offset (grid-snapped)
  const dx = Math.round((targetCenter.x - clipboard.originCenter.x) / GRID_SIZE) * GRID_SIZE;
  const dy = Math.round((targetCenter.y - clipboard.originCenter.y) / GRID_SIZE) * GRID_SIZE;

  // 3. Remap entities
  const newEntities: CanvasEntity[] = [];
  const newEntityIds: string[] = [];

  for (const entity of clipboard.entities) {
    const newId = idRemap.get(entity.id)!;
    const newLayerId = validLayerIds.has(entity.layerId) ? entity.layerId : activeLayerId;

    let newData: Record<string, unknown>;

    switch (entity.kind) {
      case 'task': {
        const td = entity.data as unknown as TaskData;
        const newTaskId = taskIdRemap.get(td.taskId)!;
        newData = { taskId: newTaskId } as unknown as Record<string, unknown>;
        break;
      }
      case 'connector': {
        const cd = entity.data as unknown as ConnectorData;
        newData = {
          ...entity.data,
          from: {
            entityId: idRemap.get(cd.from.entityId) ?? cd.from.entityId,
            side: cd.from.side,
          },
          to: {
            entityId: idRemap.get(cd.to.entityId) ?? cd.to.entityId,
            side: cd.to.side,
          },
        };
        break;
      }
      case 'group': {
        const gd = entity.data as unknown as GroupData;
        newData = {
          ...entity.data,
          memberEntityIds: gd.memberEntityIds.map(id => idRemap.get(id) ?? id),
          ...(gd.collapsedX !== undefined && { collapsedX: gd.collapsedX + dx }),
          ...(gd.collapsedY !== undefined && { collapsedY: gd.collapsedY + dy }),
        };
        break;
      }
      default:
        newData = { ...entity.data };
    }

    const newEntity: CanvasEntity = {
      ...entity,
      id: newId,
      x: entity.x + dx,
      y: entity.y + dy,
      layerId: newLayerId,
      data: newData,
    };

    newEntities.push(newEntity);
    if (entity.kind !== 'connector' && entity.kind !== 'waypoint') {
      newEntityIds.push(newId);
    }
  }

  // 3b. Validate group members: remove cross-layer references
  const entLayerMap = new Map(newEntities.map(e => [e.id, e.layerId]));
  for (let i = 0; i < newEntities.length; i++) {
    const ent = newEntities[i];
    if (ent.kind !== 'group') continue;
    const gd = ent.data as unknown as GroupData;
    const validMembers = gd.memberEntityIds.filter(id => entLayerMap.get(id) === ent.layerId);
    if (validMembers.length !== gd.memberEntityIds.length) {
      newEntities[i] = { ...ent, data: { ...ent.data, memberEntityIds: validMembers } };
    }
  }

  // 4. Create new GlobalTasks
  const newGlobalTasks: GlobalTask[] = [];
  for (const [oldTaskId, newTaskId] of taskIdRemap) {
    const originalTask = clipboard.globalTasks[oldTaskId];
    if (originalTask) {
      newGlobalTasks.push({
        ...originalTask,
        id: newTaskId,
        title: originalTask.title + ' (Kopie)',
      });
    }
  }

  return { entities: newEntities, newGlobalTasks, newEntityIds };
}
